/**
 * Which devices may be mounted on which locations, shared by the 2D and 3D assignment flows.
 *
 * The authority is the backend — Mote.associate_sensors_to_location refuses a regular mote on a
 * location with no measurement location, and TxSensor._refuse_non_bearing_vibration does the
 * same for an accelerometer channel. This exists so both UIs can say no BEFORE the user
 * completes an assignment, rather than letting the request fail and reporting it in a toast.
 *
 * Deliberately free of three.js and of any feature import: the Machine Builder loads it too,
 * and the 3D module that used to own this rule pulls the whole renderer in with it.
 */

/** Mote models that identify a vibration mote when the API sends no type string. */
const VIBRATION_MOTE_MODELS = new Set(['VM1', 'VM2', 'VM3', 'VM4', 'VM4P', 'GS', 'GSP']);

/** The device's type string, inferred from model or serial when the API did not send one. */
export function resolveSensorTypeName(sensor) {
  if (!sensor) return '';
  if (sensor.type) return sensor.type;
  if (VIBRATION_MOTE_MODELS.has(sensor.model || '')) return 'Vibration Mote';
  if (String(sensor.serial_number || '').slice(0, 2) === 'VM') return 'Vibration Mote';
  return '';
}

/**
 * Whether this device measures vibration, and therefore belongs on a bearing.
 *
 * A temperature-only mote is deliberately NOT one: the platform supports that mode precisely so
 * a mote can sit somewhere with no bearing, and the mote path has a branch for it. The same goes
 * for a TX temperature channel.
 *
 * A VSX is one physical device carrying an accelerometer, a temperature and a magnetometer
 * channel that are placed together, so the whole unit counts as a vibration device.
 */
export function isVibrationDevice(sensor) {
  if (!sensor) return false;
  const type = resolveSensorTypeName(sensor);
  if (type.includes('Vibration Mote')) return !sensor.temperature_only;
  return type === 'VSX' || type === 'Vibration Sensor';
}

export default isVibrationDevice;
